import { startAuthentication } from '@simplewebauthn/browser';

import type {
  SessionKey,
  SessionKeyStatus,
  SessionKeyOptions,
} from './sessionKeyTypes';

import { getSessionKeyUserType } from '../../config';
import { getApiClientV2 } from '../config';

/**
 * Calculate session key status from raw session key data
 */
export function calculateSessionKeyStatus(
  sessionKey: SessionKey | null | undefined
): SessionKeyStatus {
  const sessionKeyExpiresAt = sessionKey?.expiry
    ? new Date(sessionKey.expiry).getTime()
    : null;

  return {
    sessionKey: sessionKey ?? null,
    sessionKeyExpiresAt,
    isSessionKeyAuthorized:
      !!sessionKey?.expiry &&
      !!sessionKeyExpiresAt &&
      sessionKeyExpiresAt > Date.now(),
  };
}

/**
 * Ensure session key exists and has sufficient buffer time.
 * Consolidates previous forceRefreshSessionKey and requireSessionKey methods.
 *
 * This is an imperative utility function, not a hook. Can be called from anywhere.
 *
 * @param options - Session key options
 * @param options.force - Force refresh regardless of current buffer (default: false)
 * @param options.sessionKeyBufferSeconds - Required buffer time in seconds before expiry (default: 60)
 * @param options.sessionKeyTransport - Transport method for session key (default: 'cookie')
 * @param options.sessionKeyUserType - User type for session key (default: the value set in config)
 * @returns Promise<boolean> - true if a new session key was authorized, false if already had sufficient buffer
 * @throws Error if session key verification fails or user cancels WebAuthn prompt
 */
export async function requireSessionKey(options?: {
  force?: boolean;
  sessionKeyBufferSeconds?: number;
  sessionKeyTransport?: SessionKeyOptions['sessionKeyTransport'];
  sessionKeyUserType?: SessionKeyOptions['sessionKeyUserType'];
  sessionKeyExpirationSeconds?: number;
}): Promise<boolean> {
  const apiClient = getApiClientV2();
  const force = options?.force ?? false;
  const sessionKeyBufferSeconds = options?.sessionKeyBufferSeconds ?? 60; // Default 1 minute buffer
  const bufferMs = sessionKeyBufferSeconds * 1000;
  const desiredUserType =
    options?.sessionKeyUserType ?? getSessionKeyUserType();

  // Skip buffer check if force refresh requested
  if (!force) {
    // Get current session key status
    const pingResponse = await apiClient.user.ping.query();
    const status = calculateSessionKeyStatus(pingResponse?.sessionKey);

    // All apps share a single session-key cookie, but different surfaces need
    // different Turnkey user types: Collect mints a scoped `operation` key
    // (signs only the operation wallet), while web-app's admin-capable flows
    // need the root `owner` key. When the existing key's type isn't what this
    // call needs, re-mint the desired type instead of reusing it — the crossover
    // self-heals with a passkey prompt rather than throwing. A missing type in
    // the response counts as a match (back-compat with older headers/cookies).
    const userTypeMatches =
      !desiredUserType ||
      !pingResponse?.sessionKey?.turnkeyUserType ||
      desiredUserType === pingResponse.sessionKey.turnkeyUserType;

    // Check if we have enough buffer (inline previous hasSessionKeyBuffer logic)
    const hasBuffer =
      status.sessionKeyExpiresAt !== null &&
      status.sessionKeyExpiresAt - Date.now() >= bufferMs;

    if (userTypeMatches && hasBuffer) {
      return false; // Already have a matching key with sufficient buffer
    }
  }

  // Need to refresh session key - either forced or insufficient buffer
  // 1. Generate authentication options
  const { authenticationOptions, signedChallenge } =
    await apiClient.auth.generateSessionKeyAuthenticationOptions.mutate({
      sessionKeyExpirationSeconds: options?.sessionKeyExpirationSeconds,
      sessionKeyUserType: desiredUserType,
    });

  // 2. Prompt user with WebAuthn
  const assertion = await startAuthentication({
    optionsJSON: authenticationOptions,
  });

  // 3. Verify and set session key
  const result =
    await apiClient.auth.verifySessionKeyAuthenticationResponse.mutate({
      assertion,
      signedChallenge,
      sessionKeyTransport: options?.sessionKeyTransport ?? 'cookie',
    });

  if (!result.success) {
    throw new Error('Session key verification failed');
  }

  return true; // Session key was refreshed
}
